import React from "react";
import Button from "../../Button";
import { Challenge } from "../ChallengesPage";
import moment from "moment";
import styled from "styled-components";

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const Top = styled.div<{ bgColor?: string }>`
    display: flex;
    flex: 1;
    flex-direction: row;

    ${props => props.bgColor && `
        body.theme-traditional & {
            background-color: ${props.bgColor};
            color: #FFF;
        }
    `}

    body.theme-platinum & {
        background-color: var(--primary-c);
    }

    body.theme-market & {
        background-color: var(--primary);
        border-bottom: 1px solid var(--page-bg);
    }
`;

const Content = styled.div`
    font-size: 0.9rem;
    padding: 1rem 1rem 1rem 0.5rem;
    width: 100%;

    & p { margin-bottom: 0; }

    body.theme-market & {
        font-size: 0.85rem;
    }
`;

const Title = styled.h3<{ color?: string; completed?: boolean }>`
    font-family: var(--ff-title);
    font-size: 2.2rem !important;
    margin: 0 0 0.5rem 0 !important;
    text-transform: uppercase;

    ${props => props.color && `
        body.theme-traditional & {
            color: ${props.color};
        }
    `}

    body.theme-platinum & {
        font-weight: 700;
        font-size: 1.5rem !important;
    }

    body.theme-market & {
        color: ${completed => !completed ? 'var(--page-bg)' : 'var(--color-secondary-yellow)'};
        font-size: 1.5rem !important;
        font-weight: 900;
    }
`;

const Progress = styled.div`
    align-items: center;
    display: flex;
    gap: 1rem;
    margin: 0.75em 0;
`;

const ProgressBar = styled.div`
    background: #D9D9D9;
    height: 7px;
    position: relative;
    width: 80%;
`;

const ProgressBarBar = styled.span`
    background: var(--color-secondary-yellow);
    border: 1px solid #000;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
`;

const ProgressBadge = styled.div`
    align-items: center;
    background: var(--color-secondary-yellow);
    border-radius: 1rem;
    box-shadow: 0 0 5px rgba(0,0,0,0.5);
    display: flex;
    font-size: 0.9rem;
    font-weight: 700;
    height: 2rem;
    justify-content: center;
    line-height: 2rem;
    text-align: center;
    width: 5rem;
`;

const ProgressIcon = styled.i`
    color: var(--text-primary);
    font-size: 1.2rem;
    margin-right: 0.5rem;

    body.theme-platinum & {
        color: #000;
    }
    
    body.theme-market & {
        color: var(--page-bg);
    }
`;

const ExpiryDate = styled.p`
    font-size: 0.85em;
    margin-top: 0.25rem;
`;

const Buttons = styled.div`
    display: flex;
`;

const Image = styled.img`
    height: auto;
    width: 100%;
`;

interface Props {
    challenge: Challenge;
    completed: boolean;
    onSelect: (challenge: Challenge) => void;
    onActivate: (id: number) => void;
}

const ChallengeGridItem: React.FC<Props> = ({ challenge, completed, onSelect, onActivate }) => {
    const date = moment(challenge.expiry).format('DD [de] MMMM [de] YYYY');

    return (
        <Container>
            <Top bgColor={challenge.bg_color.traditional}>
                {challenge.image && (<div>
                    {['traditional', 'market', 'platinum'].map((concept) => (
                        challenge.image[concept] && <Image key={concept} src={challenge.image[concept]} alt={challenge.name} className={`d-${concept}-only`} />
                    ))}
                </div>)}

                <Content>
                    <Title color={challenge.title_color.traditional} completed>{challenge.name}</Title>

                    <p>{challenge.description_short}</p>

                    <Progress>
                        <ProgressBar><ProgressBarBar style={{width: `${challenge.progress.current/challenge.progress.target*100}%`}} /></ProgressBar>
                        <ProgressBadge>
                            <ProgressIcon className={`fa-light fa-${getChallengeIcon(challenge.type)}`} />
                            <span>
                                {challenge.progress.current}/{challenge.progress.target}
                            </span>
                        </ProgressBadge>
                    </Progress>

                    {!completed && <ExpiryDate>Vigencia al {date}.</ExpiryDate>}
                </Content>
            </Top>

            <Buttons>
                {completed && (
                    <Button primary block small onClick={() => onSelect(challenge)}>Ver recompensa</Button>
                )}
                {!completed && (
                    <>
                        <Button secondary block small onClick={() => onSelect(challenge)}>Ver detalle</Button>
                        {!challenge.active && <Button primary block small onClick={() => onActivate(challenge.id)}>Activar reto</Button>}
                    </>
                )}
            </Buttons>
        </Container>
    );
};

const getChallengeIcon = (type: string) => {
    switch (type) {
        case 'movie':
            return 'clapperboard';
        case 'product':
            return 'popcorn';
        case 'special_event':
            return 'star';
        default:
            return null;
    }
}

export default ChallengeGridItem;